import React, {useMemo, useState, useCallback, useEffect, useRef} from 'react';
import {Animated, Easing, StyleSheet, Text, View} from 'react-native';
import {useRemScale} from 'lib/remScale';

import { TYPE, FONTS } from 'style/typography';
import { onChipsHeight } from './tutorLayout';

const TUTOR_INPUT_BOTTOM_REM = 82;
const TUTOR_TRANSCRIPT_BOTTOM_REM = TUTOR_INPUT_BOTTOM_REM + 104;

/**
 * Simple transcript renderer for 1:1 tutor mode.
 * Renders above the text input, below the mic prompt/overlay area.
 */
export default function LiveKitTranscriptPanel({sourceKey = '', sourceText = '', wholeTutorReplies = false}) {
  const [width, setWidth] = useState(720);
  const [line, setLine] = useState('');
  const lastKeyRef = useRef('');
  const lastSourceTextRef = useRef('');
  const lineBufferRef = useRef('');
  const lineRevealAnimRef = useRef(new Animated.Value(1));
  const lineScaleAnimRef = useRef(new Animated.Value(1));
  const lastAnimatedLineRef = useRef('');
  const maxPlayerLines = 3;
  const {rem} = useRemScale();
  // The suggestion row sits between this line and the input bar: the line rises above however many rows it takes
  // (owner round 14.2).
  const [chipsHeight, setChipsHeightState] = useState(0);
  useEffect(() => onChipsHeight(setChipsHeightState), []);
  const lineRevealAnim = lineRevealAnimRef.current;
  const lineScaleAnim = lineScaleAnimRef.current;

  const onLayout = useCallback((e) => {
    const w = e?.nativeEvent?.layout?.width;
    if (typeof w === 'number' && w > 0) setWidth(w);
  }, []);

  const fontSize = rem(TYPE.lg);
  const maxChars = useMemo(() => {
    // Rough estimate for monospace-like chunking. Prevents lines from being "too sensitive"
    // to overflow while still keeping behavior predictable across browsers.
    // NOTE: We intentionally use a *smaller* avg char width so the subtitle line can
    // visually reach closer to the right edge before wiping. (0.6 was too conservative
    // for our font rendering and caused early wipes.)
    const avgCharPx = fontSize * 0.5;
    return Math.max(20, Math.floor(width / avgCharPx));
  }, [width, fontSize]);

  const participant = useMemo(() => {
    const key = String(sourceKey || '');
    // LiveKitSDK sets keys like `${participant}:${id}` where participant is 'player' or 'tutor'.
    return key.split(':')[0] || '';
  }, [sourceKey]);
  const isPlayer = participant === 'player';

  const computeDelta = useCallback((prevFull, nextFull) => {
    const prev = (prevFull || '');
    const next = (nextFull || '');
    if (!next) return '';
    if (!prev) return next;
    if (next.startsWith(prev)) return next.slice(prev.length);
    // fallback: longest common prefix
    let i = 0;
    const max = Math.min(prev.length, next.length);
    while (i < max && prev[i] === next[i]) i++;
    return next.slice(i);
  }, []);

  const appendSubtitleStyle = useCallback((current, delta) => {
    const d = (delta || '').replace(/\s+/g, ' ');
    if (!d.trim()) return current;

    // Split into tokens preserving spaces, so we append words naturally.
    const tokens = d.split(/(\s+)/).filter(t => t.length > 0);
    let lineBuf = current || '';

    for (const tok of tokens) {
      const next = `${lineBuf}${tok}`;
      if (next.length > maxChars) {
        // Wipe line completely and start fresh with this token (subtitle-style).
        lineBuf = '';
        // If token itself is too long, truncate it to fit.
        lineBuf = tok.length > maxChars ? tok.slice(0, maxChars) : tok;
      } else {
        lineBuf = next;
      }
    }

    // Avoid leading spaces after wipes
    return lineBuf.replace(/^\s+/, '');
  }, [maxChars]);

  const appendPlayerUpToFourLines = useCallback(
    (current, delta) => {
      const maxTotalChars = maxChars * maxPlayerLines;
      const d = (delta || '').replace(/\s+/g, ' ');
      if (!d.trim()) return current;

      // Split into tokens preserving spaces, so we append words naturally.
      const tokens = d.split(/(\s+)/).filter(t => t.length > 0);
      let buf = current || '';

      for (const tok of tokens) {
        const next = `${buf}${tok}`;
        if (next.length > maxTotalChars) {
          // After the user has "filled" ~4 lines, it's OK to start fresh.
          buf = '';
          buf = tok.length > maxTotalChars ? tok.slice(0, maxTotalChars) : tok;
        } else {
          buf = next;
        }
      }

      return buf.replace(/^\s+/, '');
    },
    [maxChars, maxPlayerLines],
  );

  useEffect(() => {
    const key = String(sourceKey || '');
    const nextFull = String(sourceText || '');

    // If speaker/segment changed, start fresh.
    if (key !== lastKeyRef.current) {
      lastKeyRef.current = key;
      lastSourceTextRef.current = '';
      lineBufferRef.current = '';
      setLine('');
    }

    // Text mode: the tutor's reply arrives written and whole. Subtitle wiping raced through it and
    // left only its last line on screen (owner item 9), so show all of it.
    if (wholeTutorReplies && !isPlayer) {
      lastSourceTextRef.current = nextFull;
      lineBufferRef.current = nextFull.trim();
      setLine(nextFull.trim());
      return;
    }

    const delta = computeDelta(lastSourceTextRef.current, nextFull);
    lastSourceTextRef.current = nextFull;
    if (!delta) return;

    const nextLine = isPlayer
      ? appendPlayerUpToFourLines(lineBufferRef.current, delta)
      : appendSubtitleStyle(lineBufferRef.current, delta);
    lineBufferRef.current = nextLine;
    setLine(nextLine);
  }, [
    sourceKey,
    sourceText,
    computeDelta,
    appendSubtitleStyle,
    appendPlayerUpToFourLines,
    isPlayer,
    wholeTutorReplies,
  ]);

  useEffect(() => {
    const next = String(line || '').trim();

    if (!next) {
      lastAnimatedLineRef.current = '';
      return;
    }

    const previous = lastAnimatedLineRef.current;
    if (previous === next) return;
    if (
      previous &&
      next.startsWith(previous) &&
      next.length - previous.length < 18
    ) {
      lastAnimatedLineRef.current = next;
      return;
    }
    lastAnimatedLineRef.current = next;

    lineRevealAnim.setValue(0);
    lineScaleAnim.setValue(0.985);
    Animated.parallel([
      Animated.timing(lineRevealAnim, {
        toValue: 1,
        duration: 280,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: true,
      }),
      Animated.spring(lineScaleAnim, {
        toValue: 1,
        friction: 7,
        tension: 120,
        useNativeDriver: true,
      }),
    ]).start();
  }, [line, lineRevealAnim, lineScaleAnim]);

  if (!line) return null;

  const lineAnimatedStyle = {
    opacity: lineRevealAnim.interpolate({
      inputRange: [0, 1],
      outputRange: [0.35, 1],
    }),
    transform: [
      {
        translateY: lineRevealAnim.interpolate({
          inputRange: [0, 1],
          outputRange: [rem(8), 0],
        }),
      },
      {
        scale: lineScaleAnim,
      },
    ],
  };
  const speakerLabel = isPlayer ? 'You' : 'Tutor';
  const speakerChipStyle = isPlayer
    ? styles.speakerChipPlayer
    : styles.speakerChipTutor;

  return (
    <View
      style={[
        styles.container,
        {bottom: rem(TUTOR_TRANSCRIPT_BOTTOM_REM) + (chipsHeight ? chipsHeight + 8 : 0), paddingHorizontal: rem(12)},
      ]}
      pointerEvents="none">
      <Animated.View style={[styles.inner, lineAnimatedStyle]} onLayout={onLayout}>
        <View style={styles.metaRow}>
          <View style={[styles.speakerChip, speakerChipStyle]}>
            <Text style={styles.speakerChipText}>{speakerLabel}</Text>
          </View>
        </View>
        <Text
          style={[styles.line, {
            fontSize,
            fontFamily: FONTS.body,
            lineHeight: rem(25),
          }]}
          // Keep tutor transcript "subtitle-style" single-line.
          // For the user's transcript, allow up to 3 wrapped lines before we start wiping.
          numberOfLines={isPlayer ? maxPlayerLines : wholeTutorReplies ? 8 : 1}
          ellipsizeMode="clip"
        >
          {line}
        </Text>
      </Animated.View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    left: 0,
    right: 0,
    zIndex: 50,
    alignItems: 'center',
  },
  // Match input bar width (maxWidth 720) and padding.
  inner: {
    width: '100%',
    maxWidth: 720,
    paddingHorizontal: 14,
    paddingVertical: 12,
    borderRadius: 16,
    backgroundColor: 'rgba(27,36,53,0.66)',
    borderWidth: 1,
    borderColor: 'rgba(197,232,54,0.24)',
    shadowColor: '#000',
    shadowOpacity: 0.4,
    shadowRadius: 20,
    shadowOffset: {width: 0, height: 10},
    overflow: 'hidden',
  },
  metaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    marginBottom: 7,
  },
  speakerChip: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 999,
    borderWidth: 1,
  },
  speakerChipTutor: {
    backgroundColor: 'rgba(197,232,54,0.14)',
    borderColor: 'rgba(197,232,54,0.36)',
  },
  speakerChipPlayer: {
    backgroundColor: 'rgba(124,193,255,0.16)',
    borderColor: 'rgba(124,193,255,0.40)',
  },
  speakerChipText: {
    color: '#E1F88A',
    fontWeight: '700',
    fontSize: TYPE.xs,
    fontFamily: FONTS.body,
    letterSpacing: 0.4,
  },
  line: {
    color: 'rgba(246,251,255,0.98)',
    fontWeight: '500',
    letterSpacing: 0.2,
  },
});

